<template>
  <div
    class="etx-container Container"
    v-if="downloads?.length > 0"
    :class="[paragraphClassList]"
  >
    <div class="etx-center Center">
      <div class="etx-text Text" :class="entranceClassList">
        <p class="font-h2">{{ category.name }}</p>
      </div>

      <div class="etx-spacer Spacer h-50"></div>

      <div class="etx-flex Flex gv-200 tab-l:gv-0">
        <div class="etx-flex__item Flex__item tab-s:c-10 tab-m:c-9 tab-l:c-4">
          <div v-if="false">
            <!-- Smaller title -->
            <div class="etx-text Text" :class="entranceClassList">
              <p class="font-h4 font-color-primary">
                {{ category.name }}
              </p>
            </div>

            <div class="etx-spacer Spacer h-25"></div>
          </div>

          <div
            v-if="downloads?.some((download) => download?.type?.isHighlight)"
            class="etx-item-list ItemList download"
          >
            <a
              v-for="download in downloads.filter((d) => d?.type?.isHighlight)"
              class="etx-item Item ItemLink"
              :href="download?.url?.path"
              :title="download?.name"
              target="_blank"
              :class="entranceClassList"
            >
              <div class="etx-item__icon Item__icon">
                <div class="etx-icon Icon larger">
                  <SpriteSymbol
                    :name="brochureIcon"
                    style="width: 3.5rem; height: 3.5rem"
                  />
                </div>
              </div>

              <div class="etx-item__text Item__text">
                <p class="font-weight-bold font-size-large">
                  {{ download?.name }}
                </p>

                <div class="etx-button Button minimal">
                  <div class="etx-button__text Button__text">
                    <span>{{ downloadLabel }}</span>
                  </div>
                  <div class="etx-button__icon Button__icon">
                    <i class="far fa-arrow-down-to-bracket"></i>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>

        <div class="etx-flex__item Flex__item tab-l:c-7 desk-s:c-6 tab-l:ml-1">
          <div
            v-if="downloads?.some((download) => !download?.type?.isHighlight)"
            class="DownloadList"
            :class="entranceClassList"
          >
            <div class="DownloadList__header">
              <div class="etx-text Text">
                <p class="font-size-small font-color-medium">
                  {{ $texts('downloads_listing.factsheets', 'Factsheets') }}
                </p>
              </div>
            </div>

            <div class="DownloadList__content">
              <div
                v-for="download in downloads.filter(
                  (d) => !d?.type?.isHighlight,
                )"
                class="Download"
              >
                <div class="Download__content">
                  <span class="font-weight-bold">{{ download?.name }}</span>
                  <!-- Todo download type here -->
                  <span
                    v-if="download?.description"
                    class="font-size-small font-color-medium"
                    >{{ download?.description }}</span
                  >
                </div>

                <div class="Download__button">
                  <div class="etx-button Button minimal">
                    <div class="etx-button__text Button__text">
                      <span>{{ downloadLabel }}</span>
                    </div>
                    <div class="etx-button__icon Button__icon">
                      <i class="far fa-arrow-down-to-bracket"></i>
                    </div>
                  </div>
                </div>
                <a
                  class="Download__link"
                  :href="download?.url?.path"
                  :title="download?.name"
                  target="_blank"
                ></a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="etx-container Container"
    :class="paragraphClassList"
    v-else-if="isEditing"
  >
    <div class="etx-center Center">
      <p class="editor-placeholder">
        {{ noDownloadsText }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { integer } from '@elastic/elasticsearch/lib/api/types'

const { $texts } = useNuxtApp()
const downloadLabel = computed(() =>
  $texts('downloads_listing.download', 'Download'),
)
const noDownloadsText = computed(() =>
  $texts(
    'downloads_listing.no_downloads',
    'Keine Downloads verfügbar. Bitte überprüfen Sie, ob die Downloads vorhanden sind und eine Kategorie haben.',
  ),
)

const { options, isEditing } = defineBlokkli({
  bundle: 'downloads_listing_category',
  globalOptions: ['spacingTop', 'spacingBottom', 'backgroundColor'],
})

const paragraphClassList = computed(() => {
  const classList = []

  if (options.value.spacingTop) {
    classList.push(options.value.spacingTop)
  }

  if (options.value.spacingBottom) {
    classList.push(options.value.spacingBottom)
  }

  if (options.value.backgroundColor !== 'white') {
    classList.push(options.value.backgroundColor)
  }

  return classList
})

const entranceClassList = computed(() => {
  const classList = []

  if (!isEditing) {
    classList.push('will-appear', 'move-up')
  }

  return classList
})

const brochureIcon = computed(() => {
  if (options.value.backgroundColor === 'bg-secondary') {
    return 'icons_Sammlung_negativ_Broschuere_offen'
  }

  return 'icons_Sammlung_positive_Broschuere_offen'
})

const props = defineProps<{
  category: {
    name: string
    id: string | integer
  }
}>()

const categoryId = props.category.id
const { data: downloads } = await useAsyncData(
  `downloadsCategory-${categoryId}`,
  async () => {
    const downloadsCategory = await useGraphqlQuery('downloadsCategory', {
      category: categoryId,
    }).then(
      (v) =>
        v?.data?.entityQuery?.items as Array<{
          name: string
          description: string
          category: {
            name: string
            weight: integer
          }
          type: {
            name: string
            weight: integer
            isHighlight: boolean
          }
          url: {
            path: string
          }
        }>,
    )

    return downloadsCategory
  },
)
</script>
